import type { Logger } from '../log/log';
import { decodeEnvelope, encodeEnvelope } from './cborEnvelope';
import { type CursorStore, MemoryCursorStore } from './cursorStore';
import { isDeviceGroupConversationId } from '../sync/deviceGroup';

// The Go BE (services/api/internal/sync/envelope.go) declares the
// envelope as a CBOR map with INTEGER keys (1-9):
//   1 = v, 2 = conversation_id, 3 = epoch, 4 = kind,
//   5 = sender_device, 6 = seq, 7 = hlc, 8 = payload, 9 = content_hash
//
// We previously tried cbor-x as the encoder, but its default emit
// shape tripped fxamacker/cbor's decoder with `bad_envelope` even
// though both libraries claim RFC 8949 compliance. Replaced with a
// tight hand-rolled codec in ./cborEnvelope.ts that only implements
// the major types this envelope needs (uint, byte string, text
// string, map) — fully deterministic, ~150 lines, and guaranteed-
// compatible with the Go decoder.

// The MLS SDK ships `epoch`, `seq`, and `hlc.wall_ms` as `number`
// in `MessageEnvelope` (see node_modules/ping-openmls-sdk/dist/
// types-*.d.cts). Our local `Envelope` must be structurally
// assignable to it, so the numeric fields are typed as `number`
// only — `decodeEnvelope` already emits `number`, and any internal
// bigint code paths funnel through `toSafeNumber` before reaching
// this surface.
export interface Envelope {
  v: number;
  conversation_id: Uint8Array;
  epoch: number;
  kind: 'Application' | 'Commit' | 'Welcome' | 'Proposal' | 'KeyPackage';
  sender_device: Uint8Array;
  seq: number;
  hlc: { wall_ms: number; logical: number };
  payload: Uint8Array;
  content_hash: Uint8Array;
}

export interface Device {
  device_id: Uint8Array;
  user_id: Uint8Array;
  key_package: Uint8Array;
}

/**
 * JSON shape of `GET /v1/messages?conv=&cursor=&limit=` (the
 * `FetchResponse` defined in `services/message-service/src/store/fetch.rs`).
 * Envelopes arrive base64url-encoded so the JSON wrapper stays text-safe;
 * we decode each back to a CBOR blob and parse via `decodeEnvelope`.
 */
interface FetchResponseJson {
  envelopes: string[];
  next_cursor?: string;
  has_more: boolean;
}

/**
 * Handle returned from `Transport.subscribe`. Matches the SDK 0.2.0+
 * shape (`{ unsubscribe(): void }`) so this Transport remains
 * structurally assignable to the SDK's expected `transport` arg.
 */
export interface SubscribeHandle {
  unsubscribe(): void;
}

/** @deprecated Use `SubscribeHandle.unsubscribe()`. */
export type Unsubscribe = () => void;

/**
 * Transport surface the SDK consumes. Mirrors the v0.5.x `ping-openmls-sdk`
 * Transport interface — keep these signatures in lockstep so this class
 * stays structurally assignable to `MessagingClient.init({ transport })`.
 *
 * `revokeDeviceRemote` is an optional addition (SDK 0.6+ will call it from
 * `revoke_device`; older SDKs ignore it) that lets the auth-layer server
 * invalidate the revoked device's KeyPackages + refuse future envelopes.
 * Implemented here as POST /v1/devices/{deviceId}/revoke.
 */
export interface Transport {
  send(envelope: Envelope): Promise<void>;
  /**
   * Pull events for one conversation since `cursor`. Empty cursor = "from
   * the beginning". Returns at-most-`limit` envelopes in (epoch ASC,
   * server_seq ASC) order; caller pages via subsequent calls with the
   * server-supplied next cursor (the SDK does this internally).
   */
  fetchSince(conversationId: Uint8Array, cursor: Uint8Array, limit: number): Promise<Envelope[]>;
  subscribe(onMessage: (envelope: Envelope) => void): SubscribeHandle;
  discoverDevices(userId: Uint8Array): Promise<Device[]>;
  revokeDeviceRemote?(deviceId: Uint8Array): Promise<void>;
  /**
   * Scheduled-send hooks (backend "send even if the app is closed"). Optional
   * so non-Ping transports / older builds satisfy the interface. See the
   * concrete `PingTransport` methods for semantics.
   */
  scheduleNextSend?(sendAtISO: string): void;
  listScheduledMessages?(conversationIdHex: string): Promise<ScheduledMessageMeta[]>;
  cancelScheduledMessage?(id: string): Promise<void>;
}

/**
 * Server-side metadata for one pending scheduled message. The server returns
 * NO plaintext (the envelope is opaque), so the host keeps its own
 * `send_at → preview text` mirror to label the pending item.
 */
export interface ScheduledMessageMeta {
  /** Opaque server handle (base64url); pass to `cancelScheduledMessage`. */
  readonly id: string;
  /** When it fires, as epoch ms. */
  readonly sendAtMs: number;
  /** When it was queued, as epoch ms (stable ordering). */
  readonly createdAtMs: number;
}

export interface ApiClient {
  get<T>(path: string, opts?: any): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: any): Promise<T>;
  put<T>(path: string, body?: unknown, opts?: any): Promise<T>;
  delete<T>(path: string, opts?: any): Promise<T>;
}

export interface SocketClient {
  connect(url: string, options?: any): {
    send(data: string | ArrayBuffer): void;
    close(): void;
    onMessage(handler: (data: string | ArrayBuffer) => void): void;
    onClose(handler: (code?: number, reason?: string) => void): void;
    onReconnect(handler: () => void): void;
  };
}

interface PingTransportDeps {
  api: ApiClient;
  socket: SocketClient;
  log: Logger;
  // Absolute WebSocket URL (e.g. `wss://openmls.ping.live/v1/ws`).
  // If omitted, defaults to `${window.location.protocol→ws/wss}//${host}/v1/ws`
  // which is correct only when the front-end is served from the same
  // origin as the API. In dev (Vite on `localhost:3000`) the BE lives
  // on a different host so this MUST be set explicitly.
  wsUrl?: string;
  /**
   * Per-conversation cursor persistence. Defaults to in-memory so
   * existing call-sites keep working unchanged; production hosts pass
   * an IndexedDB- or SQLite-backed implementation so cursors survive
   * page reload / app restart and the WS resume frame can replay them.
   * See `./cursorStore.ts` for the interface.
   */
  cursorStore?: CursorStore;
  /**
   * Soft cap on the `seenHashes` LRU used for client-side content_hash
   * dedup. Was previously an unbounded `Set<string>` — a 24-hour
   * session in a busy chat would accumulate thousands of hex strings
   * forever. Default 5000 covers the practical "last few minutes of
   * traffic" window; the BE's own UNIQUE(conversation_id, kind,
   * content_hash) index (migration 0008) is the authoritative dedup.
   */
  seenHashesCap?: number;
  /**
   * Called once-per-conv the first time the BE returns 404 on a
   * `GET /v1/messages?conv=` or `POST /v1/messages` request. The
   * host is expected to evict that conv from its local chat list /
   * Dexie row so the orphan disappears from the UI.
   *
   * Why this exists: after a `StorageCorruption` recovery wipe (or a
   * deeper cross-device de-sync), the local chat list can hold
   * conversation rows whose underlying MLS group state is gone. The
   * SDK still tries to send / sync them; the BE 404s; without auto-
   * pruning the user sees a broken row forever. The callback gives
   * the host a single point to surgically remove the affected conv
   * without tearing down the whole session.
   *
   * Receives the **hex** conversation id (matches how the chat-list
   * adapters key their rows on web + macOS).
   */
  onConversationOrphaned?: (conversationIdHex: string) => void;
}

const DEFAULT_SEEN_HASHES_CAP = 5_000;

// Listener-leak detection threshold. Crossed when a subscriber array
// grows beyond the count any legitimate use-case would need; logs a
// single warn so production telemetry can flag the leaking caller.
// See `PingTransport.debugListenerCounts()` for the dev-side harness
// that hosts call to assert subscribers go back to baseline between
// mount/unmount.
const LISTENER_LEAK_THRESHOLD = 32;

// Reconcile passes a server-listed conversation may stay un-joined before we
// log a loud `stuck_unjoined` error. The reconcile runs ~every 20s, so ~5 passes
// ≈ 100s of "the server says you're a member but your Welcome never arrived".
const UNJOINED_STUCK_THRESHOLD = 5;

export class PingTransport implements Transport {
  private readonly api: ApiClient;
  private readonly socket: SocketClient;
  private readonly log: Logger;
  private readonly wsUrlOverride: string | null;
  private readonly cursorStore: CursorStore;
  private readonly seenHashesCap: number;
  /**
   * Bounded LRU of content_hash hex prefixes we've already delivered.
   * Insertion order = recency; eviction = `keys().next().value` (FIFO,
   * close enough to LRU for our "drop duplicates that arrived in the
   * last ~minute" use case). The BE's content_hash UNIQUE index is the
   * authoritative dedup; this is just a fast-path for the very common
   * "same envelope arrives via both fetchSince and subscribe" race.
   */
  private readonly seenHashes = new Set<string>();
  private socketHandle: ReturnType<SocketClient['connect']> | null = null;
  private messageHandlers: Array<(envelope: Envelope) => void> = [];
  /**
   * BE-pushed account-events (text-frame JSON over the same
   * `/v1/stream` WS — produced by ws-fanout's
   * `/internal/push/account` endpoint when the Go API emits a
   * per-account broadcast such as `{type:"device_added", ...}`).
   * Distinct from `messageHandlers` because those carry CBOR
   * envelopes, not JSON. Hosts subscribe via `onAccountEvent`.
   */
  private accountEventHandlers: Array<(event: Record<string, unknown>) => void> = [];
  // Maps conversation_id (hex) → recipient device_id (hex) for the
  // *next* Welcome envelope the SDK emits on that conversation.
  // The MLS SDK's Transport.send(envelope) interface doesn't pass a
  // recipient hint, but the BE's send handler requires Welcomes to
  // carry `?recipient=<device_id>` so it knows which group_members
  // row to insert. Hosts (mlsConversation.ts) prime this map before
  // calling conv.addMembers() — the entry is consumed on first match.
  //
  // The value is a list to support multi-device fanout per
  // docs/architecture/multi-device.md "Per-device fan-out": one
  // Welcome envelope is addressed to ALL of the peer's joining
  // devices in a single POST via a comma-separated `?recipient=`
  // query (BE supports both single and list forms).
  private welcomeRecipients = new Map<string, readonly string[]>();
  // One-shot "route the next Application send to the scheduled-send queue"
  // flag (RFC3339 send_at). The MLS SDK has no encrypt-without-send, so a
  // scheduled message is produced by calling `conv.send(bytes)` (which
  // encrypts + advances the ratchet as pre-encryption requires) with this
  // flag set — `send()` then POSTs the opaque envelope to
  // `/v1/messages/scheduled` for deferred fan-out instead of `/v1/messages`.
  // Set via `scheduleNextSend()` immediately before the `conv.send()` call;
  // consumed on the next Application send.
  private scheduledSendAt: string | null = null;
  // Conversation ids the BE has 404'd at least once on
  // `GET /v1/messages?conv=`. Most commonly these are the SDK's own
  // "device-self" groups (deterministic `ffdc…` prefix) — local-only
  // MLS groups the SDK creates during `init` for drafts / device-
  // internal state. The SDK's `syncConversations` iterates ALL its
  // known convs and unconditionally calls `fetchSince` on each, so
  // without this cache every reload re-hits the same dead endpoint
  // and dumps a 404 into the network panel.
  //
  // Skipping the HTTP call entirely on a known-dead conv is purely a
  // client-side noise mitigation — the SDK still believes the conv
  // exists locally, but our return-empty short-circuit produces the
  // same observable behaviour as the BE's 404 (no envelopes to
  // process) without the network round-trip.
  //
  // In-memory only: a reload re-discovers the dead-list on first
  // request. We deliberately don't persist this — the cost of a
  // single 404 per reload per dead conv is much smaller than the
  // risk of permanently blacklisting a conv that DID exist but was
  // briefly unreachable.
  private deadConversationIds = new Set<string>();
  // Consecutive-404 counter per conversation. A SINGLE 404 is frequently
  // transient (replica lag right after a join, a briefly-unreachable shard), so
  // we only orphan a conversation after `ORPHAN_404_THRESHOLD` consecutive 404s
  // with no successful fetch/send in between. Reset on any success. This is the
  // fix for "a real chat vanishes on a momentary blip and can't recover until
  // reload".
  private orphan404Counts = new Map<string, { count: number; firstAt: number }>();
  private static readonly ORPHAN_404_THRESHOLD = 5;
  // Authoritative set of conversation ids (lowercased hex) the BACKEND currently
  // lists this device as a member of — fed from the host's `GET /v1/conversations`
  // reconcile via `setServerConversations`. `null` until the first successful
  // reconcile. Purpose: tell a conversation that is GENUINELY GONE server-side
  // (absent from this set — a backend rebuild/reset deleted it, or we were
  // removed) apart from one that is merely lagging (still listed → a transient
  // 404 we must NOT act on). A genuinely-gone conversation is retired on its
  // FIRST 404 instead of after the slow consecutive-failure threshold, so a
  // diverged backend self-heals in ~one tick with no 404 storm and NO manual
  // client wipe.
  private serverConversationIds: Set<string> | null = null;
  // Per-conversation send cooldown (epoch ms). After a send fails against a
  // conversation the BE doesn't have, we skip further sends to it for a short
  // window so a best-effort emitter (read pointers, receipts, device-group
  // personal state) can't hammer `POST /v1/messages` on every keystroke. Self-
  // healing: it expires on its own and clears on the next success/revive.
  private sendCooldownUntil = new Map<string, number>();
  private static readonly SEND_COOLDOWN_MS = 15_000;
  // In ADDITION to the consecutive-count threshold, the 404 streak must SPAN at
  // least this long before we orphan. A rapid burst of 404s during a brief
  // shard/replica outage or right after a join can hit the count in well under a
  // second; requiring the streak to also last this long means only a genuinely,
  // persistently unreachable conversation is soft-hidden. A live chat that 404s
  // on momentary lag gets a success in between (which resets the streak) and is
  // never pruned.
  private static readonly ORPHAN_404_MIN_STREAK_MS = 60_000;
  // DELIVERY DIAG (A): per-conversation count of consecutive reconcile passes a
  // server-listed conversation has stayed un-joined (Welcome never delivered).
  // Surfaces a loud `stuck_unjoined` error at the threshold so the "group shows
  // as a DM" root cause (no join → no MLS meta) is diagnosable from the console.
  private unjoinedPasses = new Map<string, number>();
  private readonly onConversationOrphaned?: (conversationIdHex: string) => void;

  // Set once `dispose()` runs (session teardown / account switch). A disposed
  // transport hard-drops any late-arriving WS frame and refuses new subscriptions
  // so a socket that was mid-flight when the old account signed out can never
  // deliver into the new account's handlers. See `dispose()`.
  private disposed = false;

  constructor(deps: PingTransportDeps) {
    this.api = deps.api;
    this.socket = deps.socket;
    this.log = deps.log;
    this.wsUrlOverride = deps.wsUrl ?? null;
    this.cursorStore = deps.cursorStore ?? new MemoryCursorStore();
    this.seenHashesCap = deps.seenHashesCap ?? DEFAULT_SEEN_HASHES_CAP;
    if (deps.onConversationOrphaned) {
      this.onConversationOrphaned = deps.onConversationOrphaned;
    }
  }

  /**
   * Mark a conv as orphaned. Adds to the in-memory dead-list (so
   * future fetchSince calls short-circuit without a network round-
   * trip) AND notifies the host so it can prune the conv from its
   * local chat list. Idempotent — repeated calls for the same conv
   * fire the host callback at most once per session.
   */
  private markConversationOrphaned(conversationIdHex: string): void {
    const lc = conversationIdHex.toLowerCase();
    if (this.deadConversationIds.has(lc)) return;

    // Device groups (`ffdc…`) are device-local MLS groups never registered with
    // the BE — they are NOT real chats and must never be orphaned/hidden. Their
    // best-effort sends are quieted by the send cooldown instead.
    const isDeviceGroup = lc.length >= 4 && lc.slice(0, 4) === 'ffdc';
    if (isDeviceGroup) return;

    // AUTHORITATIVE GONE: we have a known server membership list and this
    // conversation is NOT on it → it genuinely no longer exists for this device
    // (a backend rebuild/reset deleted it, or we were removed). Retire it on the
    // FIRST failure — no need to wait out the conservative threshold, which is
    // what produced the "404 storm until manual client wipe" after a backend
    // restart. Still revivable: if the conversation reappears in a later server
    // list, `setServerConversations` / `reviveConversation` clears the dead flag.
    const knownAbsent =
      this.serverConversationIds !== null && !this.serverConversationIds.has(lc);

    if (!knownAbsent) {
      // CONSERVATIVE ORPHANING: for a conversation the server STILL lists (or that
      // we haven't reconciled yet), require N consecutive 404s across a sustained
      // window before declaring it dead — a transient replica-lag 404 must NOT
      // prune a live chat. A success / reconcile revive resets the counter.
      const now = Date.now();
      const prev = this.orphan404Counts.get(lc);
      const entry =
        prev === undefined
          ? { count: 1, firstAt: now }
          : { count: prev.count + 1, firstAt: prev.firstAt };
      this.orphan404Counts.set(lc, entry);
      const streakMs = now - entry.firstAt;
      if (
        entry.count < PingTransport.ORPHAN_404_THRESHOLD ||
        streakMs < PingTransport.ORPHAN_404_MIN_STREAK_MS
      ) {
        this.log.debug('transport.conversation.orphan_pending', {
          event: 'transport.conversation.orphan_pending',
          conversationIdPrefix: lc.slice(0, 8),
          count: entry.count,
          threshold: PingTransport.ORPHAN_404_THRESHOLD,
          streakMs,
          minStreakMs: PingTransport.ORPHAN_404_MIN_STREAK_MS,
        });
        return;
      }
    } else {
      this.log.info('transport.conversation.orphan_authoritative', {
        event: 'transport.conversation.orphan_authoritative',
        conversationIdPrefix: lc.slice(0, 8),
        msg: 'server no longer lists this conversation — retiring',
      });
    }
    this.orphan404Counts.delete(lc);
    this.deadConversationIds.add(lc);
    this.cursorStore.delete(lc).catch(() => {
      /* best-effort */
    });
    try {
      this.onConversationOrphaned?.(lc);
    } catch (err) {
      this.log.warn('transport.onConversationOrphaned threw', {
        event: 'transport.onConversationOrphaned.failed',
        message: (err as Error).message,
      });
    }
  }

  /**
   * Reset the transient-404 counter after a successful fetch/send for a
   * conversation, so intermittent 404s spread across a healthy session never
   * accumulate to the orphan threshold.
   */
  private noteConversationAlive(conversationIdHex: string): void {
    const lc = conversationIdHex.toLowerCase();
    this.orphan404Counts.delete(lc);
    this.sendCooldownUntil.delete(lc);
  }

  /**
   * Record the authoritative set of conversations the BACKEND lists this device
   * as a member of (from the host's `GET /v1/conversations` reconcile). This is
   * what lets the client re-sync to server truth after a backend rebuild/reset
   * WITHOUT a manual local-state wipe: a conversation the server no longer lists
   * is retired on its first 404 (see `markConversationOrphaned`), and a
   * conversation the server DOES still list is revived (any stale dead / 404 /
   * cooldown flag is cleared) so it resumes immediately. Call it only after a
   * SUCCESSFUL reconcile (an empty/failed fetch must not be treated as "the
   * server has nothing").
   */
  setServerConversations(hexes: readonly string[]): void {
    const next = new Set(hexes.map((h) => h.toLowerCase()));
    this.serverConversationIds = next;
    for (const lc of next) {
      if (this.deadConversationIds.delete(lc)) {
        this.log.info('transport.conversation.revived', {
          event: 'transport.conversation.revived',
          conversationIdPrefix: lc.slice(0, 8),
          via: 'serverList',
        });
      }
      this.orphan404Counts.delete(lc);
      this.sendCooldownUntil.delete(lc);
    }
  }

  /**
   * MERGE conversations into the authoritative set WITHOUT replacing it — used by
   * the membership DELTA path, which only knows the NEWLY-joined ids, not the full
   * set. Adding them keeps a just-joined conversation from being wrongly retired
   * on a transient 404 before the next full reconcile refreshes the whole set.
   */
  addServerConversations(hexes: readonly string[]): void {
    if (this.serverConversationIds === null) this.serverConversationIds = new Set();
    for (const h of hexes) {
      const lc = h.toLowerCase();
      this.serverConversationIds.add(lc);
      this.deadConversationIds.delete(lc);
      this.orphan404Counts.delete(lc);
      this.sendCooldownUntil.delete(lc);
    }
  }

  /**
   * Revive a conversation the host has re-confirmed as a LIVE membership (the
   * `App.tsx` reconcile sees it in `GET /v1/conversations`). Clears the dead
   * short-circuit + the 404 counter so `fetchSince` resumes normally WITHOUT a
   * full reload — the counterpart to `markConversationOrphaned`. Idempotent.
   */
  reviveConversation(conversationIdHex: string): boolean {
    const lc = conversationIdHex.toLowerCase();
    const wasDead = this.deadConversationIds.delete(lc);
    this.orphan404Counts.delete(lc);
    this.sendCooldownUntil.delete(lc);
    if (wasDead) {
      this.log.info('transport.conversation.revived', {
        event: 'transport.conversation.revived',
        conversationIdPrefix: lc.slice(0, 8),
      });
    }
    return wasDead;
  }

  /**
   * Force a conversation back to the UN-JOINED state so the next
   * `subscribeToConversations` replays its FULL log from the beginning. Used for
   * RE-INVITE-AFTER-REMOVAL: a member who was removed then re-added still holds
   * an advanced cursor, so the BE would only replay envelopes newer than it —
   * but the SDK needs the complete join → remove → re-Welcome sequence to settle
   * on a clean re-join. Deleting the cursor (→ `null`) makes the next subscribe
   * seed `''` and request a from-zero replay. Also clears the dead/404 verdict
   * and the un-joined pass counter. Does NOT touch the `mls.conv.*` KV mapping
   * (which must survive so the SDK rebuilds the SAME group id, not an orphan).
   */
  async resetConversationForRejoin(conversationIdHex: string): Promise<void> {
    const lc = conversationIdHex.toLowerCase();
    this.deadConversationIds.delete(lc);
    this.orphan404Counts.delete(lc);
    this.unjoinedPasses.delete(lc);
    try {
      await this.cursorStore.delete(lc);
      this.log.info('transport.conversation.resetForRejoin', {
        event: 'transport.conversation.resetForRejoin',
        conversationIdPrefix: lc.slice(0, 8),
      });
    } catch (err) {
      this.log.warn('transport.conversation.resetForRejoin.failed', {
        event: 'transport.conversation.resetForRejoin.failed',
        message: (err as Error).message,
      });
    }
  }

  /**
   * Records the recipient device for the next Welcome envelope on the
   * given conversation. Call this immediately before
   * `conv.addMembers([keyPackage])` so the BE knows whose membership
   * row to insert when the Welcome lands.
   *
   * Both ids are hex strings (32 chars for the conversation_id, 32
   * chars for the device_id — uuid-shaped, with or without hyphens).
   *
   * The entry is consumed by the next matching Welcome.send() and
   * then auto-cleared, so it's safe to over-prime without leaking.
   */
  setWelcomeRecipient(conversationIdHex: string, recipientDeviceIdHex: string): void {
    this.setWelcomeRecipients(conversationIdHex, [recipientDeviceIdHex]);
  }

  /**
   * Multi-device variant of [[setWelcomeRecipient]] — addresses ONE
   * Welcome envelope to multiple joining devices in a single
   * `POST /v1/messages?recipient=A,B,C` round-trip. Use when inviting
   * the full device fleet of a peer (canonical per
   * docs/architecture/multi-device.md "Per-device fan-out").
   */
  setWelcomeRecipients(
    conversationIdHex: string,
    recipientDeviceIds: readonly string[],
  ): void {
    if (recipientDeviceIds.length === 0) {
      this.welcomeRecipients.delete(conversationIdHex.toLowerCase());
      return;
    }
    this.welcomeRecipients.set(conversationIdHex.toLowerCase(), [...recipientDeviceIds]);
  }

  /**
   * SDK-facing variant of [[setWelcomeRecipients]] — the
   * `Transport.setNextWelcomeRecipients` slot from `@ping-openmls-sdk`.
   * Called by the SDK's `admitDeviceToChats` immediately before each
   * per-chat `send(Welcome)` so the host doesn't have to coordinate
   * priming externally. Accepts byte arrays (the wire representation)
   * and re-encodes to the host's hex/base64url form.
   *
   * Returns a Promise to match the SDK trait shape; the work itself
   * is synchronous (just a Map insert).
   */
  async setNextWelcomeRecipients(
    conversationId: Uint8Array,
    recipientDeviceIds: Uint8Array[],
  ): Promise<void> {
    const conversationIdHex = this.hashToHex(conversationId);
    const recipientsB64Url = recipientDeviceIds.map((d) => this.bytesToUrlBase64(d));
    this.setWelcomeRecipients(conversationIdHex, recipientsB64Url);
  }

  /**
   * Arm a one-shot scheduled send. Call immediately before `conv.send(bytes)`
   * to route THAT Application message to the backend scheduled-send queue
   * (delivered at `sendAtISO`, an RFC3339 timestamp) instead of immediate
   * fan-out. Consumed by the next `send()`; a no-op for Commit/Welcome.
   */
  scheduleNextSend(sendAtISO: string): void {
    this.scheduledSendAt = sendAtISO;
  }

  /**
   * List the caller's pending scheduled messages for one conversation
   * (`GET /v1/messages/scheduled`). Metadata only — the server holds opaque
   * ciphertext and returns no plaintext, so the host pairs each `id` with its
   * own preview mirror. Returns `[]` on any transport error so a failed poll
   * never breaks the composer.
   */
  async listScheduledMessages(
    conversationIdHex: string,
  ): Promise<ScheduledMessageMeta[]> {
    const convB64 = this.bytesToUrlBase64(this.hexToBytes(conversationIdHex));
    try {
      const res = await this.api.get<{
        scheduled?: Array<{ id: string; send_at: string; created_at: string }>;
      }>(
        `/v1/messages/scheduled?conversation_id=${encodeURIComponent(convB64)}`,
      );
      const rows = res.scheduled ?? [];
      const out: ScheduledMessageMeta[] = [];
      for (const row of rows) {
        const sendAtMs = Date.parse(row.send_at);
        const createdAtMs = Date.parse(row.created_at);
        if (Number.isNaN(sendAtMs)) continue;
        out.push({
          id: row.id,
          sendAtMs,
          createdAtMs: Number.isNaN(createdAtMs) ? sendAtMs : createdAtMs,
        });
      }
      // Soonest first, matching the composer's pending bar.
      out.sort((a, b) => a.sendAtMs - b.sendAtMs);
      return out;
    } catch (err) {
      this.log.warn('transport.scheduled.list.failed', {
        event: 'transport.scheduled.list.failed',
        err: (err as Error).message,
      });
      return [];
    }
  }

  /** Cancel a pending scheduled message (`DELETE /v1/messages/scheduled/{id}`). */
  async cancelScheduledMessage(id: string): Promise<void> {
    await this.api.delete(`/v1/messages/scheduled/${encodeURIComponent(id)}`);
  }

  /** base64url (no padding) — matches the backend's `RawURLEncoding` ids. */
  private toB64Url(bytes: Uint8Array): string {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) {
      bin += String.fromCharCode(bytes[i]!);
    }
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  async send(envelope: Envelope): Promise<void> {
    const contentHashHex = this.hashToHex(envelope.content_hash).slice(0, 16);

    if (this.isDuplicate(envelope)) {
      this.log.debug('transport.send dedupe', { contentHash: contentHashHex });
      return;
    }

    this.markSeen(envelope);

    this.log.debug('transport.send', {
      event: 'transport.send',
      kind: envelope.kind,
      contentHash: contentHashHex,
    });

    try {
      const serialized = this.serializeEnvelope(envelope);
      const conversationIdHex = this.hashToHex(envelope.conversation_id);
      const conversationLc = conversationIdHex.toLowerCase();
      // Short-circuit sends the BE can't accept: a conversation it has already
      // told us is gone (dead — e.g. deleted by a backend rebuild/reset), or one
      // in a brief post-failure cooldown. This is the write-side of the
      // `fetchSince` dead-conv skip: without it, every best-effort emitter (read
      // pointers, receipts, device-group state) hammered `POST /v1/messages` and
      // spammed 404 errors after the backend diverged. We fail fast (so the SDK's
      // optimistic echo can show "failed") and DON'T re-log an error — the
      // conversation self-heals (dead → revived by the reconcile's server list;
      // cooldown → expires).
      if (this.deadConversationIds.has(conversationLc)) {
        this.log.debug('transport.send.skipDead', {
          event: 'transport.send.skipDead',
          kind: envelope.kind,
          conversationIdPrefix: conversationIdHex.slice(0, 8),
        });
        this.seenHashes.delete(this.dedupeKey(envelope));
        throw new Error('conversation is not available on the server');
      }
      const cooldownUntil = this.sendCooldownUntil.get(conversationLc);
      if (cooldownUntil !== undefined && Date.now() < cooldownUntil) {
        this.log.debug('transport.send.cooldown', {
          event: 'transport.send.cooldown',
          kind: envelope.kind,
          conversationIdPrefix: conversationIdHex.slice(0, 8),
        });
        this.seenHashes.delete(this.dedupeKey(envelope));
        throw new Error('conversation send is cooling down after a recent failure');
      }
      // One-shot scheduled send: hand the opaque, freshly-encrypted
      // Application envelope to the backend scheduled-send queue for
      // deferred fan-out (delivers even after this client goes offline),
      // then return WITHOUT the normal immediate-delivery bookkeeping.
      // Restricted to Application — Commit/Welcome mutate epoch state and
      // must never be deferred.
      const scheduledAt = this.scheduledSendAt;
      if (scheduledAt !== null && envelope.kind === 'Application') {
        this.scheduledSendAt = null;
        const convB64 = this.toB64Url(envelope.conversation_id);
        await this.api.post(
          `/v1/messages/scheduled?send_at=${encodeURIComponent(scheduledAt)}` +
            `&conversation_id=${encodeURIComponent(convB64)}`,
          serialized,
          { headers: { 'Content-Type': 'application/vnd.ping.wire.v2+cbor' } },
        );
        this.log.info('transport.send.scheduled', {
          event: 'transport.send.scheduled',
          sendAt: scheduledAt,
          conversationIdPrefix: conversationIdHex.slice(0, 8),
        });
        return;
      }

      // Canonical send route is `POST /v1/messages` — the conversation
      // is identified by field 2 of the CBOR `MessageEnvelope`, not by
      // the URL. (openapi.yaml /v1/messages.) The older
      // `/v1/groups/{id}/messages` path is retired.
      let path = '/v1/messages';
      // Welcomes target one or more recipient devices — the BE refuses them
      // without `?recipient=`. The host (or the SDK's `admit_device_to_chats`)
      // primes the list immediately before `conv.addMembers()`; the BE accepts a
      // comma-separated list (multi-device fanout).
      //
      // The ADMITTING COMMIT that precedes the Welcome carries the SAME
      // `?recipient` so the BE enrolls the new device into
      // `conversation_members` at COMMIT time. Without this, a message appended
      // in the window between the admitting Commit and the addressed Welcome is
      // not fanned out live nor PUSHED to the new device — it only surfaces on
      // the device's next catch-up sync (delayed, and no push wake). The Commit
      // only PEEKS the primed list; the Welcome (the terminal send of an admit)
      // is what consumes it, so both sends of the same admit get the recipient.
      if (envelope.kind === 'Welcome' || envelope.kind === 'Commit') {
        const recipients = this.welcomeRecipients.get(conversationIdHex.toLowerCase());
        if (recipients && recipients.length > 0) {
          path += `?recipient=${encodeURIComponent(recipients.join(','))}`;
          if (envelope.kind === 'Welcome') {
            this.welcomeRecipients.delete(conversationIdHex.toLowerCase());
          }
          // DELIVERY DIAG (A): the creator/adder is POSTing to N recipient
          // devices. If a member never joins, cross-check this against their
          // `transport.subscribe.welcome_received` — no receive after this send =
          // a backend store/replay gap; no send at all = a create/invite gap.
          this.log.info('transport.send.addressed', {
            event: 'transport.send.addressed',
            kind: envelope.kind,
            conversationIdPrefix: conversationIdHex.slice(0, 8),
            recipientCount: recipients.length,
            recipientPrefixes: recipients.map((r) => r.slice(0, 8)),
          });
        } else if (envelope.kind === 'Welcome') {
          // A Commit with no primed recipient is normal (broadcast epoch change);
          // only a Welcome genuinely requires one.
          this.log.warn('transport.send welcome.no_recipient', {
            event: 'transport.send.welcome.no_recipient',
            conversationIdPrefix: conversationIdHex.slice(0, 8),
          });
        }
      }
      try {
        await this.api.post(path, serialized, {
          // Canonical v2 wire contract type per ping-openmls-sdk
          // `CONTENT_TYPE_CBOR`. The BE's message-service is tolerant
          // of the legacy `application/cbor` value but the explicit
          // versioned type is the right hint going forward (CR-6).
          headers: { 'Content-Type': 'application/vnd.ping.wire.v2+cbor' },
        });
        if (envelope.kind === 'Welcome') {
          this.log.info('transport.send.welcome.accepted', {
            event: 'transport.send.welcome.accepted',
            conversationIdPrefix: conversationIdHex.slice(0, 8),
          });
        }
        // Success → reset the transient-404 counter for this conversation.
        this.noteConversationAlive(conversationIdHex);
      } catch (postErr) {
        // Surface the BE's 4xx body so we can see exactly which
        // validation tripped — message-service returns a JSON
        // `{ error, message }` body on 400/409, and the api wrapper
        // attaches it to `NetworkError.serverError` / `.serverMessage`.
        // Without this, the SDK wraps the error as a generic
        // "http 400" and we lose the diagnostic signal.
        const e = postErr as {
          status?: number;
          statusCode?: number;
          lastStatus?: number;
          serverError?: string;
          serverMessage?: string;
          message?: string;
        };
        const status = e.statusCode ?? e.lastStatus ?? e.status;
        // A 404 `not_found` is an EXPECTED signal (the conversation is gone
        // server-side), not a bug — log it at warn so a diverged backend doesn't
        // flood the console with red errors. Everything else stays error-level.
        const isGone = status === 404 && e.serverError === 'not_found';
        if (isGone) {
          this.log.warn('transport.send.failed', {
            event: 'transport.send.failed',
            kind: envelope.kind,
            status,
            serverError: e.serverError,
            conversationIdPrefix: conversationIdHex.slice(0, 8),
            msg: 'conversation not found on server — retiring / cooling down',
          });
        } else {
          this.log.error('transport.send.failed', postErr as Error, {
            event: 'transport.send.failed',
            kind: envelope.kind,
            status,
            serverError: e.serverError,
            serverMessage: e.serverMessage,
            conversationIdPrefix: conversationIdHex.slice(0, 8),
            senderDevicePrefix: this.hashToHex(envelope.sender_device).slice(0, 8),
            envelopeVersion: envelope.v,
          });
        }

        // 404 on POST /v1/messages means the BE doesn't recognise the
        // conv id we tried to send to — same orphan condition the
        // fetchSince handler covers, just discovered via a write
        // attempt instead of a read. Start a send cooldown so a best-effort
        // emitter can't hammer this endpoint, and route it through the orphan
        // path (which retires it immediately when the server's authoritative
        // list no longer includes it). We still rethrow so the SDK's send
        // promise rejects and any local "sending..." indicator can clear.
        if (isGone) {
          this.sendCooldownUntil.set(conversationLc, Date.now() + PingTransport.SEND_COOLDOWN_MS);
          this.markConversationOrphaned(conversationIdHex);
        }

        // 409 `epoch_advanced` on a Commit means the local MLS group epoch no
        // longer matches the server's — the group is desynced (a Commit was
        // merged locally but never accepted server-side, or another device
        // committed). The SDK can't rewind a locally-merged commit, so this
        // conversation can't self-heal. Log distinctly so the host can surface
        // an actionable "recreate the group" message instead of a generic retry.
        if (status === 409 && e.serverError === 'epoch_advanced') {
          this.log.warn('transport.send.epoch_advanced', {
            event: 'transport.send.epoch_advanced',
            kind: envelope.kind,
            conversationIdPrefix: conversationIdHex.slice(0, 8),
          });
        }

        throw postErr;
      }
    } catch (error) {
      this.seenHashes.delete(this.dedupeKey(envelope));
      throw error;
    }
  }

  async fetchSince(
    conversationId: Uint8Array,
    cursor: Uint8Array,
    limit: number,
  ): Promise<Envelope[]> {
    const conversationIdHex = this.hashToHex(conversationId);

    // Short-circuit known-dead convs without making the HTTP call.
    // See `deadConversationIds` for rationale — this is the noise-
    // reduction half of the 404 handling below.
    if (this.deadConversationIds.has(conversationIdHex.toLowerCase())) {
      this.log.debug('transport.fetchSince.skipDead', {
        event: 'transport.fetchSince.skipDead',
        conversationIdPrefix: conversationIdHex.slice(0, 8),
      });
      return [];
    }

    // Device groups are device-local MLS groups the SDK auto-creates
    // for personal events (drafts, read pointers, notes) — they have
    // no peers and are never registered with the BE. The SDK's
    // `syncConversations` loop iterates ALL conversations including
    // the DG, so without this guard every page load hits the BE with
    // a fetchSince that returns 403 (before recovery backup) or 404
    // (after backup) and triggers an unnecessary orphan-prune pass.
    // DG ids are deterministic with the sentinel prefix `ffdc` (see
    // `device_group_id_for` in ping-core); skip them locally.
    if (
      conversationIdHex.length >= 4
      && conversationIdHex.slice(0, 4).toLowerCase() === 'ffdc'
    ) {
      this.log.debug('transport.fetchSince.skipDeviceGroup', {
        event: 'transport.fetchSince.skipDeviceGroup',
        conversationIdPrefix: conversationIdHex.slice(0, 8),
      });
      return [];
    }

    // The SDK passes an opaque `Uint8Array` cursor it minted from the
    // last response. We round-trip it as base64url so the BE's cursor
    // codec can decode it server-side (mirror of how the BE encodes
    // `FetchResponse.next_cursor`). An empty `Uint8Array` means "from
    // the very beginning"; we omit the query param so the BE returns
    // a default cursor.
    const cursorB64 = cursor.length === 0 ? '' : this.bytesToUrlBase64(cursor);

    this.log.debug('transport.fetchSince', {
      event: 'transport.fetchSince',
      conversationIdPrefix: conversationIdHex.slice(0, 8),
      cursorLen: cursor.length,
      limit,
    });

    const params = new URLSearchParams({ conv: conversationIdHex, limit: String(limit) });
    if (cursorB64) params.set('cursor', cursorB64);
    // Canonical sync route is `GET /v1/messages?conv=&cursor=&limit=`
    // (backend-services.md §3.3; the older /v1/sync?cursor= was retired
    // per docs/architecture/backend-contracts.md "Sync / catch-up").
    //
    // 404 here means "the BE doesn't recognise this conv id for the
    // calling device" — most commonly because the SDK's in-memory
    // conv table contains an orphaned entry from a stale catchup or
    // a previous identity (e.g. after a wipe). Treat as "no envelopes
    // to sync" rather than letting the SDK crash with `transport
    // error: NetworkError: http 404`. Drop the stored cursor so the
    // next WS resume frame doesn't re-replay this dead conv either.
    let response: FetchResponseJson;
    try {
      response = await this.api.get<FetchResponseJson>(
        `/v1/messages?${params.toString()}`,
        { headers: { Accept: 'application/json' } },
      );
      // Success → reset the transient-404 counter so a later blip starts fresh.
      this.noteConversationAlive(conversationIdHex);
    } catch (err) {
      // Status-code key varies by host: web's `core/network` exposes
      // `statusCode`, the legacy `@ping/ui` `NetworkError` uses
      // `lastStatus`, others fall back to `status`. Probe all three so
      // a 404 routes to the graceful-no-op branch regardless of which
      // api client the host wired in.
      const e = err as {
        status?: number;
        statusCode?: number;
        lastStatus?: number;
        serverError?: string;
      };
      const status = e.statusCode ?? e.lastStatus ?? e.status;
      if (status === 404) {
        // Demoted from warn → debug. The SDK populates its internal
        // conv table with self-groups (device-only groups for drafts /
        // notes / etc.) that never get registered with the BE; sync'ing
        // them is harmless and the 404 is the canonical signal to skip.
        this.log.debug('transport.fetchSince.unknown_conv', {
          event: 'transport.fetchSince.unknown_conv',
          conversationIdPrefix: conversationIdHex.slice(0, 8),
        });
        // Mark orphaned: short-circuits future fetches AND notifies the
        // host to prune the conv from its chat list.
        this.markConversationOrphaned(conversationIdHex);
        return [];
      }
      if (status === 400 && cursorB64.length > 0) {
        // BE rejected our cursor encoding. The SDK encodes its
        // SyncCursor as CBOR `{epoch, last_seq_per_device, last_hlc}`
        // but the BE wants `{1: epoch, 2: server_seq}` — different
        // wire formats. Discard the cursor and retry with an empty
        // one so the SDK still gets a fresh batch; rely on
        // contentHash dedup downstream to skip already-applied
        // envelopes. Demoted to debug to keep the console quiet.
        this.log.debug('transport.fetchSince.cursor_rejected_retrying_empty', {
          event: 'transport.fetchSince.cursor_rejected',
          conversationIdPrefix: conversationIdHex.slice(0, 8),
        });
        // Drop the persisted cursor so the next WS resume frame
        // doesn't replay the same broken state.
        this.cursorStore.delete(conversationIdHex.toLowerCase()).catch(() => {
          /* best-effort */
        });
        const retryParams = new URLSearchParams({
          conv: conversationIdHex,
          limit: String(limit),
        });
        try {
          response = await this.api.get<FetchResponseJson>(
            `/v1/messages?${retryParams.toString()}`,
            { headers: { Accept: 'application/json' } },
          );
        } catch {
          return [];
        }
      } else {
        throw err;
      }
    }

    const envelopes: Envelope[] = [];
    for (const b64 of response?.envelopes ?? []) {
      const bytes = this.urlBase64ToBytes(b64);
      try {
        envelopes.push(this.deserializeEnvelope(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer));
      } catch (err) {
        this.log.warn('transport.fetchSince decode error', { event: 'transport.fetchSince.decode_failed' });
        if (err instanceof Error) {
          this.log.warn('transport.fetchSince decode detail', { message: err.message });
        }
      }
    }

    // Persist the server-supplied cursor so the next WS connect can
    // replay since this position. Server returns the cursor whenever
    // there's at least one row in the page — empty pages leave the
    // stored cursor untouched (we're still at the same position).
    if (response?.next_cursor) {
      this.cursorStore
        .put(conversationIdHex.toLowerCase(), response.next_cursor)
        .catch((err) => {
          this.log.warn('transport.cursorStore.put failed', {
            event: 'transport.cursorStore.put_failed',
            message: (err as Error).message,
          });
        });
    }

    return envelopes.filter((env) => {
      // Welcome frames MUST be handled at the client level
      // (`client.joinConversation`), never fed into a conversation's
      // in-group `process` path. The SDK's Rust `sync_conversations`
      // loop pipes EVERY `fetchSince` result straight into
      // `process_envelope` → `Conversation::process`, which throws
      // "Welcome must be handled at client level, not in-group" the
      // moment it sees a Welcome — aborting the whole bootstrap drain
      // and failing `syncEngine.start`. Welcomes are already routed
      // correctly via the live WS `subscribe` path (→ SDK
      // `dispatchIncoming` → `joinConversation`, with the benign
      // "welcome ignored" try/catch for the devices whose KeyPackage
      // can't decrypt it), so dropping them from the catch-up batch
      // loses no joins and only removes the mis-route. This is a
      // routing filter only — no crypto semantics change.
      if (env.kind === 'Welcome') {
        this.log.debug('transport.fetchSince.skipWelcome', {
          event: 'transport.fetchSince.skipWelcome',
          conversationIdPrefix: conversationIdHex.slice(0, 8),
          contentHash: this.hashToHex(env.content_hash).slice(0, 16),
        });
        return false;
      }
      if (this.isDuplicate(env)) {
        const contentHashHex = this.hashToHex(env.content_hash).slice(0, 16);
        this.log.debug('transport.fetchSince dedupe', { contentHash: contentHashHex });
        return false;
      }
      this.markSeen(env);
      return true;
    });
  }

  /**
   * POST /v1/devices/{device_id}/revoke. The MLS-side `removeMembers`
   * commits are already handled in `MessagingClient.revoke_device`;
   * this is the auth-layer cleanup so the revoked device's KeyPackages
   * are purged + future envelopes from its JWT are 403'd. Optional in
   * the Transport interface so older SDKs that don't call it keep
   * working — but exposed publicly so settings UIs can drive a
   * "remove device" action directly.
   */
  async revokeDeviceRemote(deviceId: Uint8Array): Promise<void> {
    const deviceIdB64 = this.bytesToUrlBase64(deviceId);
    this.log.debug('transport.revokeDeviceRemote', {
      event: 'transport.revokeDeviceRemote',
      deviceIdPrefix: this.hashToHex(deviceId).slice(0, 8),
    });
    await this.api.post(`/v1/devices/${encodeURIComponent(deviceIdB64)}/revoke`, undefined);
  }

  /**
   * Tear the transport down on session end / account switch. Closes the WS
   * socket immediately (not lazily on last-unsubscribe), drops every handler,
   * and latches `disposed` so any frame already in flight from the old
   * account's connection is discarded instead of routed into the next
   * account's handlers. Idempotent.
   *
   * Called from the web/desktop session teardown after `tearDownSession()`.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.messageHandlers.length = 0;
    this.accountEventHandlers.length = 0;
    this.welcomeRecipients.clear();
    if (this.socketHandle) {
      try {
        this.socketHandle.close();
      } catch {
        /* best-effort */
      }
      this.socketHandle = null;
    }
  }

  subscribe(onMessage: (envelope: Envelope) => void): SubscribeHandle {
    if (this.disposed) {
      // Refuse to revive a disposed transport — a stale React effect or the
      // tail end of a teardown race must not reopen the old account's socket.
      return { unsubscribe: (): void => {} };
    }
    this.messageHandlers.push(onMessage);
    this.warnIfListenerLeak('messageHandlers', this.messageHandlers.length);

    if (!this.socketHandle && this.shouldConnectSocket()) {
      this.connectSocket();
    }

    return {
      unsubscribe: (): void => {
        const idx = this.messageHandlers.indexOf(onMessage);
        if (idx !== -1) {
          this.messageHandlers.splice(idx, 1);
        }

        if (this.messageHandlers.length === 0 && this.socketHandle) {
          this.socketHandle.close();
          this.socketHandle = null;
        }
      },
    };
  }

  /**
   * Subscribe to BE-pushed account-events delivered over the same
   * `/v1/stream` WS as text/JSON frames. Hosts use this for
   * per-account broadcasts (currently: `{type:"device_added",
   * device_id, label, platform, ts}` emitted on a successful
   * `/v1/auth/link-verify`). Returned handle unsubscribes the
   * listener but does NOT close the socket — that stays bound to
   * the envelope subscribers' lifecycle.
   */
  onAccountEvent(handler: (event: Record<string, unknown>) => void): SubscribeHandle {
    this.accountEventHandlers.push(handler);
    this.warnIfListenerLeak('accountEventHandlers', this.accountEventHandlers.length);
    return {
      unsubscribe: (): void => {
        const idx = this.accountEventHandlers.indexOf(handler);
        if (idx !== -1) {
          this.accountEventHandlers.splice(idx, 1);
        }
      },
    };
  }

  /**
   * Ensure the BE pushes realtime envelopes — crucially the **Welcome** — for
   * conversations this device is a member of but hasn't subscribed to yet.
   *
   * The WS resume frame only subscribes conversations we hold a cursor for
   * (`sendResumeFrame` reads `cursorStore.snapshot()`). A conversation created on
   * a SIBLING or PEER device, which we learned about out-of-band (e.g. via
   * `GET /v1/conversations`), has no cursor → is never in the resume frame → the
   * BE never adds our socket to its subscriber set → its Welcome is never
   * delivered → the SDK never joins → it never appears. This is the gap behind
   * "a chat created on my other device doesn't show up here in realtime."
   *
   * For each id we have NO cursor for, seed an EMPTY cursor ("from beginning")
   * and re-send the resume frame. The BE then subscribes our socket AND replays
   * the conversation's log from the start — including the Welcome, which flows
   * through the normal subscribe → SDK dispatch → join path (so the SDK performs
   * a real MLS join; no orphan group). Already-known conversations keep their
   * persisted cursor and are untouched. Returns the number newly subscribed.
   */
  async subscribeToConversations(conversationIdHexes: readonly string[]): Promise<number> {
    let added = 0;
    // A still-empty cursor ('') means we subscribed but the SDK has NOT joined
    // yet (no real MLS state → no advanced cursor). Track these so we RE-SEND the
    // resume frame on every reconcile until the join completes — that is what
    // re-triggers the BE's pending-Welcome delivery (ws-fanout `handle_resume`
    // sends the Welcome on a from-beginning resume). Without re-sending, a
    // Welcome that wasn't yet stored at the first resume (membership-visible-
    // before-Welcome-stored race) would never be delivered, leaving the chat
    // stuck as a phantom. Device-group ids are skipped — they never join.
    // Collect the un-joined conversations' cursors so we send a DELTA resume
    // (only these), not the full snapshot — keeps the steady-state 20s reconcile
    // O(unjoined) instead of O(total conversations) even with thousands of chats.
    const unjoinedCursors: Array<{ conversation_id: string; cursor: string }> = [];
    const unjoinedHexPrefixes: string[] = [];
    const stuckHexPrefixes: string[] = [];
    for (const hex of conversationIdHexes) {
      const lc = hex.toLowerCase();
      if (lc.length === 0) continue;
      if (isDeviceGroupConversationId(lc)) continue;
      try {
        const cur = await this.cursorStore.get(lc);
        if (cur === null) {
          await this.cursorStore.put(lc, '');
          added += 1;
          unjoinedCursors.push({
            conversation_id: this.bytesToUrlBase64(this.hexToBytes(lc)),
            cursor: '',
          });
          unjoinedHexPrefixes.push(lc.slice(0, 8));
          this.unjoinedPasses.set(lc, 1);
        } else if (cur === '') {
          unjoinedCursors.push({
            conversation_id: this.bytesToUrlBase64(this.hexToBytes(lc)),
            cursor: '',
          });
          unjoinedHexPrefixes.push(lc.slice(0, 8));
          // DELIVERY DIAG (A): count how many reconcile passes this conversation
          // has stayed un-joined. A conversation the server lists us in but whose
          // Welcome never arrives climbs here forever — the definitive signal of
          // a Welcome-delivery gap (the exact symptom behind "single-peer group
          // shows as a DM": no join → no MLS meta → renders as a 1:1).
          const passes = (this.unjoinedPasses.get(lc) ?? 0) + 1;
          this.unjoinedPasses.set(lc, passes);
          if (passes === UNJOINED_STUCK_THRESHOLD) stuckHexPrefixes.push(lc.slice(0, 8));
        }
      } catch (err) {
        this.log.warn('transport.subscribeToConversations.cursor_failed', {
          message: (err as Error).message,
        });
      }
    }
    // A conversation whose cursor advanced (joined) is no longer un-joined —
    // clear its pass counter so a future delete/re-add starts fresh.
    for (const lc of this.unjoinedPasses.keys()) {
      if (!unjoinedCursors.some((c) => c.conversation_id === this.bytesToUrlBase64(this.hexToBytes(lc)))) {
        this.unjoinedPasses.delete(lc);
      }
    }
    if (unjoinedCursors.length > 0) {
      this.log.info('transport.subscribeToConversations', {
        event: 'transport.subscribeToConversations',
        added,
        unjoined: unjoinedCursors.length,
        unjoinedPrefixes: unjoinedHexPrefixes,
      });
      this.sendResumeCursors(unjoinedCursors, 'delta');
    }
    if (stuckHexPrefixes.length > 0) {
      // ERROR level so it stands out: these conversations have been listed by the
      // server for many reconciles but never delivered their Welcome — the SDK
      // will never join them and they will render as phantom 1:1s / DMs.
      this.log.error(
        'transport.conversation.stuck_unjoined',
        new Error('conversation stuck un-joined: Welcome never delivered'),
        {
          event: 'transport.conversation.stuck_unjoined',
          conversationPrefixes: stuckHexPrefixes,
          afterPasses: UNJOINED_STUCK_THRESHOLD,
        },
      );
    }
    return added;
  }

  /**
   * Diagnostic: returns the current count of each subscriber array.
   * Hosts (or test harnesses) can sample this between mount/unmount
   * cycles to detect listener leaks — if `messageHandlers` keeps
   * growing as the user navigates between chats, somebody is
   * forgetting to call `unsubscribe()` on the returned handle.
   *
   * Cheap (O(1) per array) so this is safe to call from production
   * dashboards / Sentry breadcrumbs as well as dev tooling.
   */
  debugListenerCounts(): {
    messageHandlers: number;
    accountEventHandlers: number;
    welcomeRecipientPrimes: number;
    seenHashes: number;
  } {
    return {
      messageHandlers: this.messageHandlers.length,
      accountEventHandlers: this.accountEventHandlers.length,
      welcomeRecipientPrimes: this.welcomeRecipients.size,
      seenHashes: this.seenHashes.size,
    };
  }

  private warnIfListenerLeak(name: string, count: number): void {
    // Heuristic: 32 subscribers on the same array is far above any
    // legitimate usage pattern. Web app at steady state should be
    // ~5: useIncomingMessages, useIncomingConversations, the
    // chat-room composer (sometimes), and a couple of one-shot
    // account-event listeners during linking. If we cross the
    // threshold we're probably leaking — log once at warn level
    // so production telemetry catches it, then double the
    // threshold so we don't spam.
    if (count === LISTENER_LEAK_THRESHOLD || count === LISTENER_LEAK_THRESHOLD * 2) {
      this.log.warn('transport.listener.leak_suspected', {
        event: 'transport.listener.leak_suspected',
        listener: name,
        count,
      });
    }
  }

  async discoverDevices(userId: Uint8Array): Promise<Device[]> {
    const userIdHex = this.hashToHex(userId);

    this.log.debug('transport.discoverDevices', {
      event: 'transport.discoverDevices',
      userIdPrefix: userIdHex.slice(0, 8),
    });

    const response = await this.api.get<ArrayBuffer>(
      `/v1/users/${userIdHex}/key-package`,
      { headers: { Accept: 'application/json' } }
    );

    return this.deserializeDevices(response);
  }

  private shouldConnectSocket(): boolean {
    return true;
  }

  private connectSocket(): void {
    const wsUrl = this.getWebSocketUrl();

    this.socketHandle = this.socket.connect(wsUrl, {});

    this.socketHandle.onMessage((data) => {
      // Drop anything that arrives after teardown — the socket close is async
      // so a frame buffered before `dispose()` can still fire this callback.
      // Routing it now would leak the old account's data into the new session.
      if (this.disposed) return;
      // Info-level log on every received frame so devtools can
      // confirm BE → client delivery without needing to expand the
      // Network → WS → Frames tab. This is the canary that tells us
      // whether the realtime path is functional end-to-end.
      const byteLen = data instanceof ArrayBuffer ? data.byteLength : (typeof data === 'string' ? data.length : 0);
      this.log.debug('transport.subscribe.frame', { event: 'transport.subscribe.frame', byteLen });

      // Account-events from ws-fanout's `/internal/push/account`
      // path arrive as a JSON payload. Two cases, since ws-fanout
      // may send the frame as either Text (preferred, frame-type
      // heuristic in `stream.rs`) or Binary (legacy / pre-deploy):
      //
      //   - Text frame  : socket.ts has already JSON-parsed it →
      //                   `data` is a plain object.
      //   - Binary frame: socket.ts forwards the ArrayBuffer
      //                   verbatim. We sniff the first byte: a
      //                   `{` (0x7B) means JSON, anything else
      //                   means CBOR envelope (CBOR maps start at
      //                   0xA0-0xBF or with the indefinite
      //                   marker 0xBF; never `{`).
      //
      // In either case, route to `accountEventHandlers` and skip
      // the envelope decoder. Doing the Binary-bytes sniff makes
      // the client robust against an out-of-date ws-fanout deploy.
      const asJsonEvent = (): Record<string, unknown> | null => {
        if (
          data !== null
          && typeof data === 'object'
          && !(data instanceof ArrayBuffer)
          && !ArrayBuffer.isView(data)
        ) {
          return data as Record<string, unknown>;
        }
        if (data instanceof ArrayBuffer && data.byteLength > 0) {
          const bytes = new Uint8Array(data);
          const first = bytes[0];
          // 0x7B = `{`, 0x5B = `[`. Either marks a JSON payload.
          if (first === 0x7b || first === 0x5b) {
            try {
              return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
            } catch {
              return null;
            }
          }
        }
        return null;
      };
      const accountEvent = asJsonEvent();
      if (accountEvent !== null) {
        const eventType =
          typeof accountEvent['type'] === 'string' ? accountEvent['type'] : '?';
        this.log.info('transport.subscribe.accountEvent', {
          event: 'transport.subscribe.accountEvent',
          type: eventType,
        });
        this.accountEventHandlers.forEach((handler) => {
          try {
            handler(accountEvent);
          } catch (err) {
            this.log.error('transport.subscribe.accountEvent.handlerError', err as Error);
          }
        });
        return;
      }

      // Not an account event → it must be a binary CBOR envelope. Anything that
      // is NOT a non-empty ArrayBuffer (an empty/zero-length keepalive frame, or a
      // non-binary control frame) is never an envelope — skip it silently rather
      // than feeding it to the CBOR decoder, which would throw and spam
      // `decode_error` (the benign empty-frame case behind the noisy log).
      if (!(data instanceof ArrayBuffer) || data.byteLength === 0) {
        return;
      }

      try {
        const envelope = this.deserializeEnvelope(data as ArrayBuffer);

        if (this.isDuplicate(envelope)) {
          const contentHashHex = this.hashToHex(envelope.content_hash).slice(0, 16);
          this.log.debug('transport.subscribe dedupe', { contentHash: contentHashHex });
          return;
        }

        this.markSeen(envelope);

        // DELIVERY DIAG (A): a Welcome/Commit is what makes a member JOIN a
        // group. Log these at INFO (unlike ordinary Application messages) so a
        // stuck-unjoined conversation is diagnosable from the member's console:
        // this line present = the Welcome reached the client (so any failure to
        // join is in the SDK); absent (while the creator logged
        // `transport.send.welcome`) = a backend store/replay gap.
        if (envelope.kind === 'Welcome' || envelope.kind === 'Commit') {
          this.log.info('transport.subscribe.welcome_received', {
            event: 'transport.subscribe.welcome_received',
            kind: envelope.kind,
            conversationIdPrefix: this.hashToHex(envelope.conversation_id).slice(0, 8),
            contentHash: this.hashToHex(envelope.content_hash).slice(0, 16),
          });
        } else {
          this.log.debug('transport.subscribe.deliver', {
            event: 'transport.subscribe.deliver',
            kind: envelope.kind,
            contentHash: this.hashToHex(envelope.content_hash).slice(0, 16),
          });
        }

        this.messageHandlers.forEach((handler) => {
          try {
            handler(envelope);
          } catch (error) {
            this.log.error('transport.subscribe handler error', error as Error);
          }
        });
      } catch (error) {
        // A decode failure here silently drops the frame — if it's a Welcome,
        // that's a member that never joins. Surface it loudly (A), now WITH the
        // frame's size + first byte so a real failure is diagnosable (a genuine
        // envelope starts 0xA0–0xBF; anything else is a non-envelope frame the BE
        // shouldn't be sending on this socket). Empty frames are already skipped
        // above, so reaching here means a non-empty, non-decodable binary frame.
        const bytes = new Uint8Array(data as ArrayBuffer);
        this.log.error('transport.subscribe decode error', error as Error, {
          event: 'transport.subscribe.decode_error',
          byteLen: bytes.byteLength,
          firstByte: bytes.byteLength > 0 ? bytes[0] : -1,
        });
      }
    });

    // Emit a resume frame as soon as the WS opens AND on every reconnect.
    // The BE replays envelopes with `server_seq > cursor` per conversation
    // and also adds the connection to the conv's subscriber set (so a
    // Welcome that landed while offline is delivered live without a
    // second reconnect). See services/ws-fanout/src/stream.rs:handle_resume.
    void this.sendResumeFrame();
    this.socketHandle.onReconnect(() => {
      this.log.info('transport.subscribe reconnected');
      void this.sendResumeFrame();
    });
  }

  /**
   * Builds the resume frame from the persisted cursor store and sends
   * it as a single WS text frame. Errors during cursor read are
   * non-fatal — the SDK will fall back to its REST `fetchSince` loop
   * to catch up.
   */
  private async sendResumeFrame(): Promise<void> {
    if (!this.socketHandle) return;
    let snapshot: ReadonlyArray<{ conversationIdHex: string; cursor: string }>;
    try {
      snapshot = await this.cursorStore.snapshot();
    } catch (err) {
      this.log.warn('transport.resume.snapshot_failed', {
        event: 'transport.resume.snapshot_failed',
        message: (err as Error).message,
      });
      return;
    }
    if (snapshot.length === 0) return;
    const cursors = snapshot.map((entry) => ({
      // BE wants base64url, not hex. Round-trip through bytes.
      conversation_id: this.bytesToUrlBase64(this.hexToBytes(entry.conversationIdHex)),
      cursor: entry.cursor,
    }));
    this.sendResumeCursors(cursors, 'full');
  }

  private sendResumeCursors(
    cursors: ReadonlyArray<{ conversation_id: string; cursor: string }>,
    mode: 'full' | 'delta',
  ): void {
    if (!this.socketHandle || cursors.length === 0) return;
    const frame = JSON.stringify({ type: 'resume', cursors });
    try {
      this.socketHandle.send(frame);
      this.log.info('transport.resume.sent', {
        event: 'transport.resume.sent',
        cursorCount: cursors.length,
        mode,
      });
    } catch (err) {
      this.log.warn('transport.resume.send_failed', {
        event: 'transport.resume.send_failed',
        message: (err as Error).message,
      });
    }
  }

  private getWebSocketUrl(): string {
    // Explicit injection wins. Hosts that talk to a non-same-origin BE
    // (most dev setups) MUST pass `wsUrl` in deps; the same-origin
    // default below is only correct when the BE is colocated with the
    // front-end.
    if (this.wsUrlOverride !== null) {
      return this.wsUrlOverride;
    }
    if (typeof window !== 'undefined' && window.location) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}/v1/ws`;
    }
    return 'wss://openmls.ping.live/v1/ws';
  }

  /**
   * Dedupe key = content_hash + HLC. The SDK computes `content_hash` over the
   * PLAINTEXT only (`SHA-256(kind || payload)`), so two messages with identical
   * text ("hi", "ok") share a content_hash. Keying dedupe on the hash ALONE
   * therefore silently dropped legitimately-repeated messages (the send was
   * never POSTed, the receive was never delivered). The HLC (wall-clock +
   * logical counter) is unique per send but STABLE for a given envelope across
   * re-delivery / re-encryption, so `content_hash + hlc` dedupes true
   * duplicates while letting repeated text through.
   */
  private dedupeKey(envelope: Envelope): string {
    const hash = this.hashToHex(envelope.content_hash);
    const wall = envelope.hlc?.wall_ms;
    const logical = envelope.hlc?.logical;
    return `${hash}@${String(wall ?? '')}.${String(logical ?? '')}`;
  }

  private isDuplicate(envelope: Envelope): boolean {
    return this.seenHashes.has(this.dedupeKey(envelope));
  }

  private markSeen(envelope: Envelope): void {
    const hex = this.dedupeKey(envelope);
    if (this.seenHashes.has(hex)) {
      // Touch-to-front: delete + re-add bumps recency in the
      // insertion-ordered Set so frequently-seen hashes don't get
      // evicted before less frequent ones.
      this.seenHashes.delete(hex);
    } else if (this.seenHashes.size >= this.seenHashesCap) {
      // Evict the oldest entry. `keys().next().value` is the first
      // insertion (FIFO order on Sets), which is the best we can do
      // for true LRU without dragging in a map+linked-list helper.
      const oldest = this.seenHashes.keys().next().value;
      if (oldest !== undefined) {
        this.seenHashes.delete(oldest);
      }
    }
    this.seenHashes.add(hex);
  }

  /**
   * Test hook — returns the current `seenHashes` size so the LRU eviction
   * tests can assert the cap is respected. Marked `_` so the property is
   * visible-on-purpose and won't accidentally be used by production code.
   */
  _seenHashesSize(): number {
    return this.seenHashes.size;
  }

  private hashToHex(hash: Uint8Array): string {
    return Array.from(hash)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(bytes).toString('base64');
    }
    const binary = String.fromCharCode(...bytes);
    return btoa(binary);
  }

  /**
   * `Uint8Array` → base64url (no padding). The wire format the BE uses
   * everywhere — cursor query param, ticket id, recipient id, etc. We
   * have a plain-base64 helper above; this one strips the `+/=` chars
   * to the url-safe alphabet so callers can stitch the result into a
   * URL without `encodeURIComponent`.
   */
  private bytesToUrlBase64(bytes: Uint8Array): string {
    return this.uint8ArrayToBase64(bytes)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /** base64url string → `Uint8Array`. Tolerant of padding presence. */
  private urlBase64ToBytes(s: string): Uint8Array {
    const padded = s.replace(/-/g, '+').replace(/_/g, '/');
    const fill = (4 - (padded.length % 4)) % 4;
    const b64 = padded + '='.repeat(fill);
    // `@ping/ui`'s tsconfig doesn't pull in `@types/node`, so `Buffer`
    // is typed as a minimal stub. At runtime it's the real node Buffer
    // on vitest / RN-bridge hosts (which is itself a `Uint8Array`
    // subclass), and undefined in the browser where `atob` covers us.
    const B = (globalThis as { Buffer?: { from(s: string, enc: string): Uint8Array } }).Buffer;
    if (B !== undefined) {
      const buf = B.from(b64, 'base64');
      return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    }
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  /** Hex string → `Uint8Array`. Used for the conv-id round-trip on resume. */
  private hexToBytes(hex: string): Uint8Array {
    const clean = hex.length % 2 === 0 ? hex : `0${hex}`;
    const out = new Uint8Array(clean.length / 2);
    for (let i = 0; i < out.length; i++) {
      out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
  }

  // CBOR-encode the envelope as a map with integer keys (1-9), inner
  // HLC map keyed by "wall_ms" / "logical" text strings — exactly
  // what services/api/internal/sync/envelope.go decodes.
  private serializeEnvelope(envelope: Envelope): ArrayBuffer {
    const bytes = encodeEnvelope({
      v: envelope.v,
      conversation_id: envelope.conversation_id,
      epoch: toSafeNumber(envelope.epoch),
      kind: this.kindToU8(envelope.kind),
      sender_device: envelope.sender_device,
      seq: toSafeNumber(envelope.seq),
      hlc: {
        wall_ms: toSafeNumber(envelope.hlc.wall_ms),
        logical: envelope.hlc.logical,
      },
      payload: envelope.payload,
      content_hash: envelope.content_hash,
    });
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }

  // CBOR-decode the envelope produced by services/api/internal/sync/
  // envelope.go. Mirror of `serializeEnvelope`.
  private deserializeEnvelope(data: ArrayBuffer): Envelope {
    const decoded = decodeEnvelope(data);
    return {
      v: decoded.v,
      conversation_id: decoded.conversation_id,
      epoch: decoded.epoch,
      kind: this.u8ToKind(decoded.kind),
      sender_device: decoded.sender_device,
      seq: decoded.seq,
      hlc: decoded.hlc,
      payload: decoded.payload,
      content_hash: decoded.content_hash,
    };
  }

  private deserializeDevices(data: ArrayBuffer): Device[] {
    const devices: Device[] = [];
    const bytes = new Uint8Array(data);
    let offset = 0;

    while (offset < bytes.length) {
      if (offset + 4 > bytes.length) {
        break;
      }

      const deviceLen = new DataView(bytes.buffer, offset, 4).getUint32(0, false);
      offset += 4;

      if (offset + deviceLen > bytes.length) {
        break;
      }

      const device_id = bytes.slice(offset, offset + 32);
      offset += 32;

      const user_id = bytes.slice(offset, offset + 32);
      offset += 32;

      if (offset + 4 > bytes.length) {
        break;
      }

      const kpLen = new DataView(bytes.buffer, offset, 4).getUint32(0, false);
      offset += 4;

      const key_package = bytes.slice(offset, offset + kpLen);
      offset += kpLen;

      devices.push({ device_id, user_id, key_package });
    }

    return devices;
  }

  // Kind discriminants match the authoritative Rust enum in
  // ping-openmls-sdk/core/ping-core/src/message.rs:
  //   Application=1, Commit=2, Welcome=3, Proposal=4, KeyPackage=5
  // This is the same byte the SDK pre-images into the content_hash
  // (`content_hash = SHA256([kind_u8] || payload)`), so if the wire
  // byte we ship disagrees with what the SDK hashed, the BE's
  // content_hash check fails with bad_envelope.
  private kindToU8(kind: Envelope['kind']): number {
    switch (kind) {
      case 'Application':
        return 1;
      case 'Commit':
        return 2;
      case 'Welcome':
        return 3;
      case 'Proposal':
        return 4;
      case 'KeyPackage':
        return 5;
      default:
        throw new Error(`Unknown kind: ${kind}`);
    }
  }

  private u8ToKind(byte: number | undefined): Envelope['kind'] {
    if (byte === undefined) {
      throw new Error('Kind byte is undefined');
    }
    switch (byte) {
      case 1:
        return 'Application';
      case 2:
        return 'Commit';
      case 3:
        return 'Welcome';
      case 4:
        return 'Proposal';
      case 5:
        return 'KeyPackage';
      default:
        throw new Error(`Unknown kind byte: ${byte}`);
    }
  }

  // (Previously here: bigintToBytes + bytesToBigint helpers for the
  //  hand-rolled offset-packed binary envelope format. Both are
  //  unused now that the wire envelope is CBOR — removed to keep the
  //  surface area tight. Recover via git history if a downstream
  //  caller turns up.)
}

// Coerce bigint → number for the cbor encoder. epoch / seq / wall_ms
// arrive as `number` from the SDK in practice; only retained for the
// off-chance a caller hands us a bigint.
function toSafeNumber(value: number | bigint): number {
  if (typeof value === 'number') return value;
  return Number(value);
}
